import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import AppLayout from "@/components/AppLayout";
import { AlertCircle, CalendarClock, CheckCircle2, CreditCard, Download, FileText, Loader2, ShieldCheck, XCircle } from "lucide-react";
import { api, API_BASE, formatApiError, tokenStore } from "@/lib/api";
import { useT } from "@/lib/i18n";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import BillingDetailsBlock from "@/components/BillingDetailsBlock";
import LoginCredentialsBlock from "@/components/LoginCredentialsBlock";
import GdprBlock from "@/components/GdprBlock";

// Toont abonnements­details van de ingelogde user + opzegknop.
// Data komt van `GET /api/mollie/subscription`; de knop stuurt
// `DELETE /api/mollie/subscription` waarna de backend `subscription_status`
// op "canceled" zet en `subscription_access_until` op Mollie's laatst
// bekende `next_payment_date` — user houdt toegang tot die datum.

const PLAN_LABELS = {
  STANDARD: "Standaard",
  PREMIUM:  "Premium",
  TRIAL:    "Proefperiode",
  FREE:     "Geen abonnement",
};

function formatDate(t, iso) {
  if (!iso) return "—";
  try {
    // Mollie levert "YYYY-MM-DD"; ISO strings van onze backend hebben tijd.
    const d = iso.length === 10 ? new Date(`${iso}T00:00:00Z`) : new Date(iso);
    if (isNaN(d.getTime())) return iso;
    return new Intl.DateTimeFormat("nl-NL", { day: "numeric", month: "long", year: "numeric" }).format(d);
  } catch {
    return iso;
  }
}

function formatAmount(a) {
  if (!a || typeof a.value === "undefined") return "—";
  const n = Number(a.value);
  if (isNaN(n)) return `${a.currency || ""} ${a.value}`;
  return `€${n.toLocaleString("nl-NL", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function Row({ label, value, testid }) {
  return (
    <div className="flex justify-between items-baseline py-2 border-b border-border last:border-b-0">
      <span className="stat-label">{label}</span>
      <span className="text-sm font-mono-num" data-testid={testid}>{value}</span>
    </div>
  );
}

export default function AccountPage() {
  const t = useT();
  const { refreshMe } = useAuth();
  const [sub, setSub] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [canceling, setCanceling] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [invoices, setInvoices] = useState(null);   // null = loading, [] = geen, [...] = lijst
  const [invoicesError, setInvoicesError] = useState(null);
  const [downloadingId, setDownloadingId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await api.get("/mollie/subscription");
      setSub(data);
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || e.message || "Kon abonnement niet ophalen.");
    } finally {
      setLoading(false);
    }
  }, []);

  const loadInvoices = useCallback(async () => {
    setInvoicesError(null);
    try {
      const { data } = await api.get("/mollie/invoices");
      setInvoices(Array.isArray(data?.invoices) ? data.invoices : []);
    } catch (e) {
      setInvoicesError(formatApiError(e.response?.data?.detail) || e.message || "Kon facturen niet ophalen.");
      setInvoices([]);
    }
  }, []);

  useEffect(() => { load(); loadInvoices(); }, [load, loadInvoices]);

  const handleDownloadInvoice = async (paymentId, invoiceNumber) => {
    setDownloadingId(paymentId);
    try {
      // Bearer-token via query is niet mogelijk; we halen de PDF via fetch
      // met Authorization header en downloaden via een tijdelijke blob-link.
      const token = tokenStore.get();
      const res = await fetch(`${API_BASE}/mollie/invoices/${paymentId}.pdf`, {
        credentials: "include",
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!res.ok) {
        let msg = "Download mislukt.";
        try { const j = await res.json(); msg = formatApiError(j.detail) || msg; } catch { /* geen JSON */ }
        throw new Error(msg);
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${invoiceNumber || paymentId}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (e) {
      toast.error(e.message || "Download mislukt.");
    } finally {
      setDownloadingId(null);
    }
  };

  const handleCancel = async () => {
    setCanceling(true);
    try {
      const { data } = await api.delete("/mollie/subscription");
      const until = data?.subscription_access_until;
      const msg = until
        ? t("Abonnement opgezegd. Je behoudt toegang tot {date}.").replace("{date}", formatDate(t, until))
        : t("Abonnement opgezegd.");
      toast.success(msg);
      setConfirmOpen(false);
      await load();
      try { await refreshMe?.(); } catch { /* refreshMe is optional */ }
      // Refresh invoices too — na cancel wordt de dataset niet dynamischer
      // dan hij was, maar we willen wel het laatste factuurnummer zien.
      loadInvoices();
    } catch (e) {
      const msg = formatApiError(e.response?.data?.detail) || e.message || t("Opzeggen mislukt.");
      toast.error(msg);
    } finally {
      setCanceling(false);
    }
  };

  const tier = (sub?.tier || "FREE").toUpperCase();
  const planLabel = PLAN_LABELS[tier] || tier;
  const isPaid = tier === "STANDARD" || tier === "PREMIUM";
  const isCanceled = sub?.subscription_status === "canceled";
  // De opzegknop verschijnt zolang de user een subscription-koppeling heeft
  // en die niet al opgezegd is. We vertrouwen niet op de live Mollie-status
  // (die kan even wegvallen); de backend beslist bij DELETE alsnog correct.
  const canCancel = sub?.has_subscription && !isCanceled;
  // Betaald niveau zonder Mollie-koppeling = door de beheerder toegekend (geen incasso).
  const isManualGrant = isPaid && !sub?.has_subscription && !isCanceled;

  return (
    <AppLayout>
      <div className="max-w-3xl mx-auto" data-testid="account-page">
        <div className="mb-6">
          <div className="stat-label">{t("Account")}</div>
          <h1 className="font-display font-black text-4xl sm:text-5xl uppercase mt-1 leading-none">
            {t("Mijn abonnement")}
          </h1>
          <p className="text-sm md:text-base text-muted-foreground mt-4 max-w-2xl">
            {t("Bekijk hier de status van je jaarabonnement, de eerstvolgende incassodatum en zeg zo nodig op vóór de volgende afschrijving.")}
          </p>
        </div>

        {loading && (
          <div className="border-2 border-foreground bg-card heavy-shadow p-8 text-center" data-testid="account-loading">
            <Loader2 className="w-8 h-8 mx-auto animate-spin text-primary" />
            <p className="text-sm text-muted-foreground mt-3">{t("Abonnement wordt opgehaald…")}</p>
          </div>
        )}

        {!loading && error && (
          <div className="border-2 border-destructive bg-destructive/10 text-destructive p-4 text-sm mb-4" data-testid="account-error">
            <AlertCircle className="inline w-4 h-4 mr-2" />{error}
          </div>
        )}

        {!loading && sub && (
          <div className="border-2 border-foreground bg-card heavy-shadow p-6 md:p-8" data-testid="account-card">
            {/* Plan header */}
            <div className="flex items-start justify-between flex-wrap gap-4 mb-6">
              <div>
                <div className="stat-label">{t("Huidig plan")}</div>
                <div className="flex items-baseline gap-2 mt-1">
                  <span className="font-display font-black text-3xl uppercase" data-testid="account-tier">{planLabel}</span>
                  {isPaid && !isCanceled && !isManualGrant && (
                    <span className="inline-flex items-center gap-1 text-xs font-display uppercase tracking-widest text-positive">
                      <CheckCircle2 className="w-3.5 h-3.5" /> {t("Actief")}
                    </span>
                  )}
                  {isManualGrant && (
                    <span className="inline-flex items-center gap-1 text-xs font-display uppercase tracking-widest text-primary" data-testid="account-manual-grant-badge">
                      <CheckCircle2 className="w-3.5 h-3.5" /> {t("Toegekend door beheerder")}
                    </span>
                  )}
                  {isCanceled && (
                    <span className="inline-flex items-center gap-1 text-xs font-display uppercase tracking-widest text-destructive" data-testid="account-canceled-badge">
                      <XCircle className="w-3.5 h-3.5" /> {t("Opgezegd")}
                    </span>
                  )}
                </div>
                <p className="text-xs text-muted-foreground mt-1">{sub.email}</p>
              </div>
              {!isPaid && (
                <Link
                  to="/prijzen"
                  data-testid="account-upgrade-cta"
                  className="btn-brutal bg-primary text-primary-foreground px-4 py-2 font-display uppercase font-black text-sm inline-flex items-center gap-2"
                >
                  {t("Kies een abonnement")}
                </Link>
              )}
            </div>

            {/* Trial state — no Mollie sub, just a countdown */}
            {tier === "TRIAL" && (
              <div className="border-2 border-foreground bg-primary/5 p-4 mb-4" data-testid="account-trial-block">
                <div className="flex items-start gap-3">
                  <CalendarClock className="w-5 h-5 text-primary shrink-0 mt-0.5" />
                  <div>
                    <div className="text-sm font-semibold">{t("Je proefperiode loopt")}</div>
                    <div className="text-xs text-muted-foreground mt-0.5">
                      {t("Verloopt op {date}").replace("{date}", formatDate(t, sub.trial_ends_at))}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Paid subscription details */}
            {sub.has_subscription && (
              <>
                <div className="grid md:grid-cols-2 gap-x-8 gap-y-1 mb-6">
                  <Row label={t("Bedrag per jaar")}    value={formatAmount(sub.amount)} testid="account-amount" />
                  <Row label={t("Interval")}           value={sub.interval || "—"}      testid="account-interval" />
                  <Row
                    label={isCanceled ? t("Toegang tot") : t("Volgende afschrijving")}
                    value={formatDate(t, isCanceled ? sub.subscription_access_until : sub.next_payment_date)}
                    testid={isCanceled ? "account-access-until" : "account-next-payment"}
                  />
                  <Row label={t("Startdatum")}          value={formatDate(t, sub.upgraded_at)}  testid="account-started" />
                  <Row label={t("Mollie-status")}      value={sub.mollie_status || "—"}         testid="account-mollie-status" />
                  <Row label={t("Abonnements-ID")}     value={sub.mollie_subscription_id || "—"} testid="account-sub-id" />
                </div>

                {isCanceled && (
                  <div className="border-2 border-destructive/50 bg-destructive/5 p-4 mb-4 text-sm" data-testid="account-canceled-notice">
                    <div className="flex items-start gap-3">
                      <ShieldCheck className="w-5 h-5 text-destructive shrink-0 mt-0.5" />
                      <div>
                        <div className="font-semibold text-destructive">{t("Abonnement opgezegd")}</div>
                        <div className="text-xs text-muted-foreground mt-1">
                          {t("Er wordt geen nieuwe incasso meer uitgevoerd. Je hebt toegang tot {date}. Daarna gaat je account terug naar de gratis modus.")
                            .replace("{date}", formatDate(t, sub.subscription_access_until))}
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {sub.mollie_fetch_error && (
                  <div className="border-2 border-yellow-500 bg-yellow-500/10 text-yellow-700 dark:text-yellow-300 p-3 text-xs mb-4" data-testid="account-mollie-fetch-warning">
                    {t("Live status kon niet worden opgehaald bij Mollie — de opgeslagen gegevens worden getoond.")}
                  </div>
                )}

                {!isCanceled && canCancel && (
                  <div className="pt-4 border-t-2 border-border">
                    <div className="flex flex-wrap items-center gap-3">
                      <Link
                        to="/prijzen"
                        data-testid="account-switch-plan"
                        className="btn-brutal bg-background text-foreground border-2 border-foreground px-4 py-2 font-display uppercase font-bold text-sm inline-flex items-center gap-2"
                      >
                        <CreditCard className="w-4 h-4" /> {t("Wissel abonnement")}
                      </Link>
                      <button
                        type="button"
                        onClick={() => setConfirmOpen(true)}
                        data-testid="account-cancel-btn"
                        className="btn-brutal bg-destructive text-destructive-foreground px-4 py-2 font-display uppercase font-bold text-sm inline-flex items-center gap-2"
                      >
                        <XCircle className="w-4 h-4" /> {t("Abonnement opzeggen")}
                      </button>
                    </div>
                    <p className="text-xs text-muted-foreground mt-2">
                      {t("Opzeggen stopt de jaarlijkse incasso. Je behoudt toegang tot de eerstvolgende afschrijvingsdatum.")}
                    </p>
                  </div>
                )}
              </>
            )}

            {/* No subscription yet (FREE, or user paid but Mollie fetch has no sub_id) */}
            {isManualGrant && (
              <div className="border-2 border-foreground bg-primary/5 p-4 mb-4" data-testid="account-manual-grant-block">
                <p className="text-sm">
                  {t("Dit niveau is handmatig toegekend door de beheerder. Er loopt geen automatische incasso; je hoeft niets te doen.")}
                </p>
              </div>
            )}
            {!sub.has_subscription && tier !== "TRIAL" && !isManualGrant && (
              <div className="border-2 border-dashed border-border p-6 text-center">
                <CreditCard className="w-8 h-8 mx-auto text-muted-foreground" />
                <p className="text-sm mt-3">{t("Er is nog geen actief abonnement gekoppeld aan dit account.")}</p>
                <Link
                  to="/prijzen"
                  data-testid="account-no-sub-cta"
                  className="mt-4 inline-flex items-center gap-2 btn-brutal bg-primary text-primary-foreground px-4 py-2 font-display uppercase font-black text-sm"
                >
                  {t("Bekijk abonnementen")}
                </Link>
              </div>
            )}
          </div>
        )}

        {/* Facturenoverzicht — altijd zichtbaar zodra sub geladen is en er 1
            of meer historische betalingen zijn. Ook zichtbaar bij lege lijst
            zodat users weten dat we hun facturen bijhouden. */}
        {!loading && sub && (
          <div className="mt-6 border-2 border-foreground bg-card heavy-shadow p-6 md:p-8" data-testid="invoices-card">
            <div className="flex items-baseline justify-between mb-4">
              <div>
                <div className="stat-label">{t("Facturen")}</div>
                <h2 className="font-display font-black text-2xl uppercase mt-1">{t("Betalingshistorie")}</h2>
              </div>
              {invoices && invoices.length > 0 && (
                <span className="text-xs text-muted-foreground" data-testid="invoices-count">
                  {t("{n} facturen").replace("{n}", String(invoices.length))}
                </span>
              )}
            </div>

            {invoicesError && (
              <div className="border-2 border-destructive bg-destructive/10 text-destructive p-3 text-sm mb-3" data-testid="invoices-error">
                <AlertCircle className="inline w-4 h-4 mr-2" />{invoicesError}
              </div>
            )}

            {invoices === null && (
              <div className="py-8 text-center" data-testid="invoices-loading">
                <Loader2 className="w-6 h-6 mx-auto animate-spin text-primary" />
              </div>
            )}

            {Array.isArray(invoices) && invoices.length === 0 && !invoicesError && (
              <div className="border-2 border-dashed border-border p-6 text-center text-sm text-muted-foreground" data-testid="invoices-empty">
                <FileText className="w-6 h-6 mx-auto mb-2 opacity-60" />
                {t("Nog geen facturen. Zodra je eerste betaling binnen is, verschijnt hier de PDF-download.")}
              </div>
            )}

            {Array.isArray(invoices) && invoices.length > 0 && (
              <div className="overflow-x-auto" data-testid="invoices-table-wrapper">
                <table className="w-full text-sm" data-testid="invoices-table">
                  <thead>
                    <tr className="border-b-2 border-foreground text-left">
                      <th className="py-2 pr-4 stat-label">{t("Factuurnr.")}</th>
                      <th className="py-2 pr-4 stat-label">{t("Datum")}</th>
                      <th className="py-2 pr-4 stat-label">{t("Plan")}</th>
                      <th className="py-2 pr-4 stat-label text-right">{t("Bedrag")}</th>
                      <th className="py-2 pr-4 stat-label">{t("Methode")}</th>
                      <th className="py-2 pl-4 stat-label text-right">{t("Download")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {invoices.map((inv) => (
                      <tr
                        key={inv.payment_id}
                        className="border-b border-border last:border-b-0"
                        data-testid={`invoice-row-${inv.payment_id}`}
                      >
                        <td className="py-3 pr-4 font-mono-num" data-testid={`invoice-number-${inv.payment_id}`}>
                          {inv.invoice_number || "—"}
                        </td>
                        <td className="py-3 pr-4">{formatDate(t, inv.date)}</td>
                        <td className="py-3 pr-4">{PLAN_LABELS[inv.plan] || inv.plan || "—"}</td>
                        <td className="py-3 pr-4 font-mono-num text-right">
                          {inv.amount ? `€${Number(inv.amount.value).toLocaleString("nl-NL", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : "—"}
                        </td>
                        <td className="py-3 pr-4">
                          {inv.method
                            ? ({ ideal: "iDEAL", creditcard: "Creditcard", bancontact: "Bancontact" }[inv.method] || inv.method)
                            : "—"}
                        </td>
                        <td className="py-3 pl-4 text-right">
                          {inv.has_pdf ? (
                            <button
                              type="button"
                              onClick={() => handleDownloadInvoice(inv.payment_id, inv.invoice_number)}
                              disabled={downloadingId === inv.payment_id}
                              data-testid={`invoice-download-${inv.payment_id}`}
                              className="inline-flex items-center gap-1 text-primary hover:underline font-display uppercase font-bold text-xs disabled:opacity-60"
                            >
                              {downloadingId === inv.payment_id
                                ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                : <Download className="w-3.5 h-3.5" />}
                              {t("PDF")}
                            </button>
                          ) : (
                            <span className="text-xs text-muted-foreground italic">
                              {inv.status ? t("Status: {s}").replace("{s}", inv.status) : "—"}
                            </span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* Confirm dialog — Native `confirm` zou werken, maar we willen brutalist-styling. */}
        {confirmOpen && (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4"
            data-testid="cancel-confirm-overlay"
            onClick={(e) => { if (e.target === e.currentTarget) setConfirmOpen(false); }}
          >
            <div className="border-2 border-foreground bg-card heavy-shadow p-6 max-w-md w-full">
              <div className="flex items-start gap-3">
                <AlertCircle className="w-6 h-6 text-destructive shrink-0 mt-0.5" />
                <div>
                  <h2 className="font-display font-black text-2xl uppercase leading-tight">
                    {t("Abonnement opzeggen?")}
                  </h2>
                  <p className="text-sm text-muted-foreground mt-2 leading-relaxed">
                    {sub?.next_payment_date
                      ? t("Na opzeggen wordt er op {date} géén incasso meer uitgevoerd. Je behoudt tot die datum volledige toegang, daarna gaat je account terug naar de gratis modus.")
                          .replace("{date}", formatDate(t, sub.next_payment_date))
                      : t("Na opzeggen wordt er geen incasso meer uitgevoerd. Je behoudt toegang tot het einde van de al betaalde periode.")}
                  </p>
                </div>
              </div>
              <div className="mt-5 flex gap-2 justify-end">
                <button
                  type="button"
                  onClick={() => setConfirmOpen(false)}
                  disabled={canceling}
                  data-testid="cancel-confirm-back"
                  className="btn-brutal bg-background text-foreground border-2 border-foreground px-4 py-2 font-display uppercase font-bold text-sm"
                >
                  {t("Terug")}
                </button>
                <button
                  type="button"
                  onClick={handleCancel}
                  disabled={canceling}
                  data-testid="cancel-confirm-yes"
                  className="btn-brutal bg-destructive text-destructive-foreground px-4 py-2 font-display uppercase font-black text-sm inline-flex items-center gap-2 disabled:opacity-60"
                >
                  {canceling ? (
                    <><Loader2 className="w-4 h-4 animate-spin" /> {t("Bezig…")}</>
                  ) : (
                    <>{t("Ja, opzeggen")}</>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Factuurgegevens + Inloggegevens + AVG-blok — pas laden wanneer we
            weten wie de user is. Voor niet-anonimiseerd account. */}
        {!loading && sub && sub.email && (
          <div className="mt-6 space-y-6">
            <BillingDetailsBlock />
            <LoginCredentialsBlock currentEmail={sub.email} />
            <GdprBlock />
          </div>
        )}

        <div className="mt-6">
          <Link
            to="/"
            data-testid="account-back-to-dashboard"
            className="text-sm text-muted-foreground hover:text-foreground underline"
          >
            ← {t("Terug naar dashboard")}
          </Link>
        </div>
      </div>
    </AppLayout>
  );
}
